import React, { useContext, useState, useEffect } from 'react';
import { Editor, EditorTools, EditorUtils, EditorToolsSettings } from '@progress/kendo-react-editor';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { DatePicker, TimePicker } from '@progress/kendo-react-dateinputs';
import { RadioGroup, Switch, Input, ColorPicker } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import Loader from '../../components/Loader';
import UpdateController from '../../components/UpdateController';
import Uploader, { uploaderTypes } from '../../components/Uploader';
import { LocalizationContext } from '../../contexts';
import { useSystemMessages } from '../../hooks';
import { getLangAndWorkspace } from '../../utils/helper';
import './index.scss';

const EditorDirectionTool = (dir) => (props) => {
    const { view } = props;
    const dir_type = dir;
    const dir_type_actions = [
        { node: "paragraph", style: [{ name: 'direction', value: dir_type }] },
        { node: "heading", style: [{ name: 'direction', value: dir_type }] }
    ];
    const clear_actions = [
        { node: "paragraph", style: [{ name: 'direction', value: '' }] },
        { node: "heading", style: [{ name: 'direction', value: '' }] }
    ];
    const is_dir_type = view && view.state && EditorUtils.isAligned(view.state, dir_type_actions);

    const onClick = () => {
        if (view) {
            if (is_dir_type) {
                EditorUtils.alignBlocks(view, clear_actions);
            }
            else {
                EditorUtils.alignBlocks(view, dir_type_actions);
            }
        }
    };

    return (
        <Button
            onClick={onClick}
            togglable={true}
            selected={is_dir_type}
            title={dir}
            onMouseDown={e => e.preventDefault()}
            onPointerDown={e => e.preventDefault()}
            className={dir === 'rtl' ? 'k-button k-button-icon k-rtl k-group-end' : 'k-button k-button-icon k-rtl k-group-start'}
            style={{
                width: 'calc(1.4285714286em + 16px)',
                height: 'calc(1.4285714286em + 16px)',
                padding: '8px'
            }}
        >
            {dir === 'rtl' && <svg style={{ width: '16px', height: '16px' }} viewBox="0 0 24 24"><path d="M8 5h8v2h-2v12h-2V7h-2v12H8v-7c-.5 0-1 0-1.4-.3A3.4 3.4 0 014.8 10a3.3 3.3 0 010-2.8 3.4 3.4 0 011.8-1.8L8 5zm12 11.2a1 1 0 11-1 1.6l-3-2a1 1 0 010-1.6l3-2a1 1 0 111 1.6L18.4 15l1.8 1.2z" fill-rule="evenodd"></path></svg>}
            {dir === 'ltr' && <svg style={{ width: '16px', height: '16px' }} viewBox="0 0 24 24"><path d="M11 5h7a1 1 0 010 2h-1v11a1 1 0 01-2 0V7h-2v11a1 1 0 01-2 0v-6c-.5 0-1 0-1.4-.3A3.4 3.4 0 017.8 10a3.3 3.3 0 010-2.8 3.4 3.4 0 011.8-1.8L11 5zM4.4 16.2L6.2 15l-1.8-1.2a1 1 0 011.2-1.6l3 2a1 1 0 010 1.6l-3 2a1 1 0 11-1.2-1.6z" fill-rule="evenodd"></path></svg>}
        </Button>
    );
};

const EditorColorTool = (styleProp) => (props) => {
    const { view } = props;
    //const bgColors = view ? EditorUtils.getInlineStyles(view.state, { name: styleProp, value: /^.+$/ }) : [];
    const onChange = (event) => {
        view && EditorUtils.applyInlineStyle(view, { style: styleProp, value: event.value });
    };

    return (
        <ColorPicker
            view={'gradient'}
            icon={styleProp === 'color' ? 'edit-tools' : 'background'}
            dir='ltr'
            onChange={onChange}
            onActiveColorClick={onChange}
        />
    );
};
const EditorColorTool_bg = EditorColorTool('color');
const EditorColorTool_tc = EditorColorTool('background-color');

const InsertImageToolTemp = (props) => {
    return InsertImageTool(props, 'system_messages');
};
const InsertImageTool = (props, prefix) => {
    const [localization] = useContext(LocalizationContext);
    const [openedDialog, setOpenedDialog] = useState(false);
    const toggleDialog = () => setOpenedDialog(!openedDialog);

    const { view } = props;
    const state = view && view.state;
    const nodeType = state ? state.schema.nodes[EditorToolsSettings.image.node] : undefined;

    return <>
        <Button
            onClick={toggleDialog}
            disabled={!nodeType || !state || !EditorUtils.canInsert(state, nodeType)}
            onMouseDown={e => e.preventDefault()}
            onPointerDown={e => e.preventDefault()}
            title={localization.labels.editorImageUploader.insert_image}
            {...EditorToolsSettings.image.props}
        />
        {openedDialog && view && (
            <Uploader
                key="insertImageDialog"
                view={view}
                onClose={toggleDialog}
                imageNode={EditorToolsSettings.image.node}
                type={uploaderTypes.editorImage}
                prefix={prefix}
            />
        )}
    </>;
};

const EditorFontSizeTool = EditorTools.createStyleDropDownList({
    style: 'font-size',
    defaultItem: { text: 'Font Size', value: '' },
    items: [
        { text: '1 (0.5rem)', value: '0.5rem' },
        { text: '2 (1rem)', value: '1rem' },
        { text: '3 (1.5rem)', value: '1.5rem' },
        { text: '4 (2rem)', value: '2rem' },
        { text: '5 (2.5rem)', value: '2.5rem' },
        { text: '6 (3rem)', value: '3rem' },
        { text: '7 (3.5rem)', value: '3.5rem' },
        { text: '8 (4rem)', value: '4rem' },
        { text: '9 (4.5rem)', value: '4.5rem' },
        { text: '10 (5rem)', value: '5rem' },
        { text: '11 (5.5rem)', value: '5.5rem' },
        { text: '12 (6rem)', value: '6rem' },
        { text: '13 (6.5rem)', value: '6.5rem' },
        { text: '14 (7rem)', value: '7rem' },
        { text: '15 (7.5rem)', value: '7.5rem' },
        { text: '16 (8rem)', value: '8rem' },
        { text: '17 (8.5rem)', value: '8.5rem' },
        { text: '18 (9rem)', value: '9rem' },
        { text: '19 (9.5rem)', value: '9.5rem' },
        { text: '20 (10rem)', value: '10rem' },
    ]
});

const EditorFontFamilyTool = EditorTools.createStyleDropDownList({
    style: 'font-family',
    defaultItem: { text: 'Font Family', value: '' },
    items: [
        { text: "AlmoniNeue", value: "'AlmoniNeue'" },
        { text: "Arimo", value: "'Arimo',sans-serif" },
        { text: "Abraham", value: "'Abraham'" },
        { text: "Assistant", value: "'Assistant',sans-serif" },
        { text: "Alef", value: "'Alef',sans-serif" },
        { text: "Heebo", value: "'Heebo',sans-serif" },
        { text: "Noto Sans Hebrew", value: "'Noto Sans Hebrew'" },
        { text: "Open Sans Hebrew", value: "'Open Sans Hebrew'" },
        { text: "Lateef", value: "'Lateef',cursive" },
        { text: "Traditional Arabic", value: "'traditional arabic'" },
        { text: "Arial", value: "'Arial',sans-serif" },
        { text: "Courier New", value: "'Courier New',sans-serif" },
        { text: "Tahoma", value: "'Tahoma',sans-serif" },
        { text: "Times New Roman", value: "'Times New Roman',sans-serif" }
    ]
});

export default function SystemMessagesManagement({ match }) {
    const [localization] = useContext(LocalizationContext);
    const { content_language, labels } = localization;
    const { systemMessagesType } = match.params;
    const {
        isMessageLoading,
        errorMessageLoading,
        message,
        clearErrorMessageLoading,
        getMessage,
        updateMessage
    } = useSystemMessages();
    const [messageData, setMessageData] = useState(null);
    const [updateData, setUpdateData] = useState(false);
    const [showSaveDialog, setShowSaveDialog] = useState(false);

    useEffect(() => {
        if (updateData) {
            setShowSaveDialog(true);
        }
        else {
            loadMessage();
        }
    }, [content_language]);

    useEffect(() => {
        loadMessage();
    }, [systemMessagesType]);

    useEffect(() => {
        setMessageData({ ...message });
        setUpdateData(false);
        // Extract lang from combo ID for comparison
        const { lang: currentLang } = getLangAndWorkspace(content_language);
        if (message && message.lang !== currentLang) {
            loadMessage();
        }
    }, [message]);

    const loadMessage = () => {
        setShowSaveDialog(false);
        if (systemMessagesType && content_language) {
            // Extract lang and workspace from combo ID
            const { lang, workspace } = getLangAndWorkspace(content_language);
            getMessage(systemMessagesType, lang, workspace);
        }
    };

    const update = () => {
        setShowSaveDialog(false);
        var fetch = updateMessage(messageData);
        fetch.then(() => {
            setUpdateData(false);
        });
        return fetch;
    };

    const updateItem = (newData) => {
        var newItem = { ...messageData, ...newData };
        setMessageData(newItem);
        setUpdateData(true);
    };

    const editorToolsList = [
        [EditorTools.Bold, EditorTools.Italic, EditorTools.Underline, EditorTools.Strikethrough],
        [EditorTools.Subscript, EditorTools.Superscript],
        [EditorTools.AlignRight, EditorTools.AlignCenter, EditorTools.AlignLeft, EditorTools.AlignJustify],
        [EditorDirectionTool('rtl'), EditorDirectionTool('ltr')],
        [EditorTools.Indent, EditorTools.Outdent],
        [EditorTools.OrderedList, EditorTools.UnorderedList],
        [EditorTools.Link, EditorTools.Unlink, InsertImageToolTemp],
        [EditorTools.InsertTable],
        [EditorTools.AddRowBefore, EditorTools.AddRowAfter, EditorTools.AddColumnBefore, EditorTools.AddColumnAfter],
        [EditorTools.DeleteRow, EditorTools.DeleteColumn, EditorTools.DeleteTable],
        [EditorTools.MergeCells, EditorTools.SplitCell],
        [EditorTools.CleanFormatting],
        [EditorTools.Redo, EditorTools.Undo],
        [EditorColorTool_tc], [EditorColorTool_bg],
        [EditorFontSizeTool], [EditorFontFamilyTool]
    ];

    const editorToolsListMin = [
        [EditorTools.Bold],
        [EditorTools.AlignRight, EditorTools.AlignCenter, EditorTools.AlignLeft, EditorTools.AlignJustify],
        [EditorDirectionTool('rtl'), EditorDirectionTool('ltr')],
        [EditorTools.Link, EditorTools.Unlink],
        [EditorTools.CleanFormatting],
        [EditorTools.Redo, EditorTools.Undo]
    ];

    const editorStyle = {
        maxHeight: '640px',
        direction: messageData && messageData.lang === 'en' ? 'ltr' : 'rtl',
        textAlign: messageData && messageData.lang === 'en' ? 'left' : 'right'
    };

    const formatDate = (inputDate) => {
        let date = '';
        if (inputDate) {
            date = new Date(inputDate);
            let year = date.getFullYear();
            let month = date.getMonth() + 1;
            month = month < 10 ? '0' + month : month;
            let day = date.getDate();
            let hours = date.getHours();
            let minutes = date.getMinutes();
            minutes = minutes < 10 ? '0' + minutes : minutes;
            date = day + '/' + month + '/' + year + ' ' + hours + ':' + minutes;
        }
        return date;
    };

    const isSomeDey = messageData &&
        messageData.scheduleFrom &&
        messageData.scheduleTo &&
        messageData.scheduleFrom.getDate() === messageData.scheduleTo.getDate() &&
        messageData.scheduleFrom.getMonth() === messageData.scheduleTo.getMonth() &&
        messageData.scheduleFrom.getFullYear() === messageData.scheduleTo.getFullYear();

    return <div className="app-system_messages_management">
        {isMessageLoading && <Loader />}
        {errorMessageLoading && <Dialog onClose={() => { clearErrorMessageLoading(false); }} title={labels.loading_error_title}><p>{labels.loading_error_message}</p></Dialog>}
        {showSaveDialog && <Dialog onClose={() => { update(); }} title={false} >
            <p>{labels.unsaved_changes.message}</p>
            <DialogActionsBar>
                <button className="k-button k-primary" onClick={() => { update(); }}>{labels.unsaved_changes.save}</button>
                <button className="k-button" onClick={() => { loadMessage(); }}>{labels.unsaved_changes.continue_without_saving}</button>
            </DialogActionsBar>
        </Dialog>}
        <UpdateController lastEditor={message ? message.lastUpdatedUser : ''} lastUpdate={formatDate(message ? message.lastUpdatedDate : '')} hasChanges={updateData} update={update}>
            {messageData && <div className="app-system_messages_management-contaner">
                <div className="app-system_messages_management-title">
                    <div className="app-system_messages_management-title--start">{labels.systemMessagesManagement['title_' + messageData.type]}</div>
                    <div className="app-system_messages_management-title--end">
                        <span style={{ margin: '0 10px' }}>{messageData.isActive ? labels.systemMessagesManagement.on : labels.systemMessagesManagement.off}</span>
                        <Switch onChange={() => {
                            updateItem({ isActive: !messageData.isActive });
                        }} checked={messageData.isActive} /></div>
                </div>
                <hr />

                <div className="app-system_messages_management-title">{labels.systemMessagesManagement.target_device_title}</div>
                <RadioGroup data={[
                    { label: labels.systemMessagesManagement.target_device_desktop, value: 'desktop' },
                    { label: labels.systemMessagesManagement.target_device_mobile, value: 'mobile' },
                    { label: labels.systemMessagesManagement.target_device_all, value: 'all' }
                ]}
                    layout='horizontal'
                    value={messageData.targetDevice}
                    onChange={({ value }) => {
                        updateItem({ targetDevice: value });
                    }}
                />

                <div className="app-system_messages_management-title">{labels.systemMessagesManagement.user_type_title}</div>
                <RadioGroup data={[
                    { label: labels.systemMessagesManagement.user_type_teacher, value: 'teacher' },
                    { label: labels.systemMessagesManagement.user_type_guest, value: 'guest' }
                ]}
                    layout='horizontal'
                    value={messageData.userType}
                    onChange={({ value }) => {
                        updateItem({ userType: value });
                    }}
                />

                {messageData.type === 'dominate' && <>
                    <div className="app-system_messages_management-subtitle">{labels.systemMessagesManagement.input_title}</div>
                    <Input value={messageData.title || ''} onChange={({ value }) => {
                        var val = value.length > 200 ? value.substring(0, 200) : value;
                        updateItem({ title: val });
                    }}></Input>
                    <div className="app-system_messages_management-input_info">{`${(messageData.title || '').length}/200`}</div>
                </>}
                <div className="app-system_messages_management-title">{labels.systemMessagesManagement.input_text}</div>
                <Editor
                    dir={labels.dir}
                    value={messageData.content || ''}
                    style={messageData.type === 'elite' ? {} : { maxWidth: '666px' }}
                    onChange={(event) => {
                        updateItem({ content: event.html });
                    }}

                    tools={messageData.type === 'elite' ? editorToolsListMin : editorToolsList}
                    contentStyle={messageData.type === 'elite' ? { ...editorStyle, maxHeight: '640px' } : { ...editorStyle, maxHeight: '70vh' }}
                    defaultEditMode="div"
                />
                {messageData.type === 'elite' && <div>
                    <div className="app-system_messages_management-title">{labels.systemMessagesManagement.message_click}</div>
                    <RadioGroup data={[
                        { label: labels.systemMessagesManagement.message_click_none, value: '' },
                        { label: labels.systemMessagesManagement.message_click_blank, value: '_blank' },
                        { label: labels.systemMessagesManagement.message_click_top, value: '_top' },
                        { label: labels.systemMessagesManagement.message_click_popup, value: 'popup' }
                    ]}
                        layout='horizontal'
                        value={messageData.linkTarget}
                        onChange={({ value }) => {
                            updateItem({ linkTarget: value });
                        }}
                    />
                    {(messageData.linkTarget === '_blank' || messageData.linkTarget === '_top') && <div>
                        <div className="app-system_messages_management-title">{labels.systemMessagesManagement.input_link}</div>
                        <Input style={{ direction: 'ltr', textAlign: 'left' }} value={messageData.link || ''} onChange={({ value }) => {
                            var val = value.length > 200 ? value.substring(0, 200) : value;
                            updateItem({ link: val });
                        }}></Input>
                        <div className="app-system_messages_management-input_info">{`${(messageData.link || '').length}/200`}</div>
                    </div>}
                    {messageData.linkTarget === 'popup' && <div>
                        <div className="app-system_messages_management-title">{labels.systemMessagesManagement.input_popup}</div>
                        <Editor
                            dir={labels.dir}
                            style={{ maxWidth: '666px' }}
                            value={messageData.popupContent || ''}
                            onChange={(event) => {
                                updateItem({ popupContent: event.html });
                            }}
                            tools={editorToolsList}
                            contentStyle={{ ...editorStyle, maxHeight: '70vh' }}
                            defaultEditMode="div"
                        />
                    </div>}
                </div>}
                {messageData.type === 'dominate' && <>
                    <div className="app-system_messages_management-title">
                        {labels.systemMessagesManagement.show_button}
                        <span style={{ margin: '0 10px' }}>
                            <Switch onChange={(event) => {
                                updateItem({ showButton: event.value });
                            }} checked={messageData.showButton} />
                        </span>
                    </div>
                    {messageData.showButton && <>
                        <div className="app-system_messages_management-title">{labels.systemMessagesManagement.input_button_text}</div>
                        <Input value={messageData.buttonText || ''} onChange={({ value }) => {
                            var val = value.length > 100 ? value.substring(0, 100) : value;
                            updateItem({ buttonText: val });
                        }}></Input>
                        <div className="app-system_messages_management-input_info">{`${(messageData.buttonText || '').length}/100`}</div>
                        <div className="app-system_messages_management-title">{labels.systemMessagesManagement.button_click}</div>
                        <RadioGroup data={[
                            { label: labels.systemMessagesManagement.button_click_none, value: '' },
                            { label: labels.systemMessagesManagement.button_click_blank, value: '_blank' },
                            { label: labels.systemMessagesManagement.button_click_top, value: '_top' }
                        ]}
                            layout='horizontal'
                            value={messageData.linkTarget}
                            onChange={({ value }) => {
                                updateItem({ linkTarget: value });
                            }}
                        />
                        {(messageData.linkTarget === '_blank' || messageData.linkTarget === '_top') && <div>
                            <div className="app-system_messages_management-title">{labels.systemMessagesManagement.input_link}</div>
                            <Input style={{ direction: 'ltr', textAlign: 'left' }} value={messageData.link || ''} onChange={({ value }) => {
                                var val = value.length > 200 ? value.substring(0, 200) : value;
                                updateItem({ link: val });
                            }}></Input>
                            <div className="app-system_messages_management-input_info">{`${(messageData.link || '').length}/200`}</div>
                        </div>}
                    </>}
                    <div className="app-system_messages_management-title">
                        {labels.systemMessagesManagement.show_hide_message_button}
                        <span style={{ margin: "0 10px" }}>
                            <Switch
                            onChange={(event) => {
                                updateItem({ showHideMessageButton: event.value });
                            }}
                            checked={messageData.userType === 'guest' ? messageData.showHideMessageButton = false : messageData.showHideMessageButton}
                            />
                        </span>
                    </div>
                    {messageData.showHideMessageButton && 
                        <>
                            <div className="app-system_messages_management-title">{labels.systemMessagesManagement.input_button_text}</div>
                            <Input
                                value={messageData.hideMessageButtonText || ""}
                                onChange={({ value }) => {
                                    var val =
                                    value.length > 100
                                        ? value.substring(0, 100)
                                        : value;
                                    updateItem({ hideMessageButtonText: val });
                                }}
                            ></Input>
                            <div className="app-system_messages_management-input_info">{`${(messageData.hideMessageButtonText || '').length}/100`}</div>
                        </>
                    }
                </>}
                <div className="app-system_messages_management-title">
                    {labels.systemMessagesManagement.schedule}
                    <span style={{ margin: '0 10px' }}>
                        <Switch onChange={(event) => {
                            var date = null;
                            if (event.value) {
                                date = new Date();
                            }
                            updateItem({
                                scheduleFrom: date,
                                scheduleTo: date
                            });
                        }} checked={!(messageData.scheduleFrom === null || messageData.scheduleTo === null)} />
                    </span>
                </div>

                {!(messageData.scheduleFrom === null || messageData.scheduleTo === null) && <div>
                    <div className="app-system_messages_management-title">{labels.systemMessagesManagement.from}</div>
                    <DatePicker
                        value={messageData.scheduleFrom}
                        format={"dd/MM/y"}
                        max={messageData.scheduleTo}
                        onChange={({ value }) => {
                            updateItem({ scheduleFrom: value });
                        }}
                    />
                    <TimePicker
                        value={messageData.scheduleFrom}
                        format={"HH:mm"}
                        {...(isSomeDey ? { max: messageData.scheduleTo } : {})}
                        onChange={({ value }) => {
                            updateItem({ scheduleFrom: value });
                        }}
                    />
                    <div className="app-system_messages_management-title">{labels.systemMessagesManagement.to}</div>
                    <DatePicker
                        value={messageData.scheduleTo}
                        format={"dd/MM/y"}
                        min={messageData.scheduleFrom}
                        onChange={({ value }) => {
                            updateItem({ scheduleTo: value });
                        }}
                    />
                    <TimePicker
                        value={messageData.scheduleTo}
                        format={"HH:mm"}
                        {...(isSomeDey ? { min: messageData.scheduleFrom } : {})}
                        onChange={({ value }) => {
                            updateItem({ scheduleTo: value });
                        }}
                    />
                </div>}
                <div className="app-system_messages_management-title"></div>
            </div>}
        </UpdateController>
    </div>;
};